import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchBusinessInfo, fetchBusinessType } from '../bridge';
import Logo from '../components/Logo';
import InfoTab from '../components/manage/InfoTab';
import CategoryManager from '../components/manage/CategoryManager';
import StoreManager from '../components/manage/StoreManager';
import PortfolioManager from '../components/manage/PortfolioManager';
import SettingsTab from '../components/manage/SettingsTab';
import '../styles/Manage.css';
import '../styles/Subscription.css';

const Manage = () => {
    const { userEmail, isSubscribed, refreshSubscription } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('info');
    const [businessType, setBusinessType] = useState('');
    const [accountId, setAccountId] = useState(null);
    const [businessName, setBusinessName] = useState('');
    const [currency, setCurrency] = useState('$');
    const [loading, setLoading] = useState(true);
    const [showUpgradeSuccess, setShowUpgradeSuccess] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        const init = async () => {
            // If returning from Stripe success, refresh subscription from DB
            if (params.get('upgraded') === 'true') {
                await refreshSubscription();
                navigate('/manage', { replace: true });
                setShowUpgradeSuccess(true);
                setTimeout(() => setShowUpgradeSuccess(false), 4000);
            } else {
                // Always sync subscription status on mount
                refreshSubscription();
            }

            const [typeData, info] = await Promise.all([
                fetchBusinessType(userEmail),
                fetchBusinessInfo(userEmail),
            ]);
            if (typeData) setBusinessType(typeData.business_type || '');
            if (info) {
                setAccountId(info.id);
                setBusinessName(info.business_name || '');
                setCurrency(info.business_currency || '$');
            }
            setLoading(false);
        };

        init();
    }, [userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

    const renderMenuTab = () => {
        if (loading) return <div className="manage-loading">Loading…</div>;
        if (!businessType) return (
            <div className="manage-card">
                <p style={{ color: '#9C8677', fontSize: 14 }}>
                    Business type not set. Go to the <strong>Info</strong> tab and ensure your account is fully set up.
                </p>
            </div>
        );
        if (businessType === 'Store') return <StoreManager email={userEmail} currency={currency} isSubscribed={isSubscribed} />;
        if (businessType === 'Service Provider') return <PortfolioManager email={userEmail} isSubscribed={isSubscribed} />;
        return <CategoryManager email={userEmail} currency={currency} isSubscribed={isSubscribed} />;
    };

    const viewPageUrl = accountId
        ? `/business/${accountId}/${encodeURIComponent(businessName || 'my-business')}`
        : null;

    const menuTabLabel = businessType === 'Service Provider' ? 'Portfolio' : businessType === 'Store' ? 'Store' : 'Menu';

    return (
        <div className="manage-page">
            <Logo showAuthButtons />

            <div className="manage-header">
                <div className="manage-header-left">
                    <h1>Dashboard</h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                        <p style={{ margin: 0 }}>{userEmail}</p>
                        <span className={`sub-badge ${isSubscribed ? 'pro' : 'free'}`}>
                            {isSubscribed ? '⭐ Pro' : 'Free'}
                        </span>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    {viewPageUrl && (
                        <Link to={viewPageUrl} target="_blank" className="manage-view-link">
                            View my page ↗
                        </Link>
                    )}
                    <button
                        className={`settings-icon-btn${activeTab === 'settings' ? ' active' : ''}`}
                        title="Settings"
                        onClick={() => setActiveTab(prev => prev === 'settings' ? 'info' : 'settings')}
                        aria-label="Settings"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="3" />
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Upgrade banner for free users */}
            {!loading && !isSubscribed && (
                <div style={{ maxWidth: 880, margin: '20px auto 0', padding: '0 24px' }}>
                    <div className="sub-banner">
                        <div className="sub-banner-text">
                            <h3>You're on the free plan</h3>
                            <p>Upgrade to Pro for up to 9 categories and 20 products each.</p>
                        </div>
                        <button
                            className="sub-upgrade-btn"
                            onClick={async () => {
                                const { supabase } = await import('../supabase');
                                try {
                                    const { data, error } = await supabase.functions.invoke('create-checkout-session', { body: { email: userEmail } });
                                    if (error || !data?.url) throw new Error();
                                    window.location.href = data.url;
                                } catch {
                                    alert('Could not open checkout. Please try again.');
                                }
                            }}
                        >
                            Upgrade to Pro ⭐
                        </button>
                    </div>
                </div>
            )}

            {activeTab !== 'settings' && (
                <div className="manage-tabs-wrap">
                    <div className="manage-tabs">
                        <button className={`manage-tab${activeTab === 'info' ? ' active' : ''}`} onClick={() => setActiveTab('info')}>Info</button>
                        <button className={`manage-tab${activeTab === 'menu' ? ' active' : ''}`} onClick={() => setActiveTab('menu')}>{menuTabLabel}</button>
                    </div>
                </div>
            )}

            <div className="manage-content">
                {activeTab === 'info' && <InfoTab email={userEmail} />}
                {activeTab === 'menu' && renderMenuTab()}
                {activeTab === 'settings' && <SettingsTab email={userEmail} />}
            </div>

            {showUpgradeSuccess && (
                <div className="sub-success-toast">
                    ⭐ You're now a Pro member! All limits unlocked.
                </div>
            )}
        </div>
    );
};

export default Manage;
